import { supabaseClient, isSupabaseConfigured } from '../lib/supabase'

export interface DemoRequest {
  id: string
  name: string
  email: string
  company: string
  phone: string
  employees: string
  message: string
  status: 'nueva' | 'contactada' | 'convertida' | 'descartada'
  createdAt: string
  notes: string
}

// ============================================
// SUPABASE-FIRST with localStorage fallback
// ============================================

const STORAGE_KEY = 'cricket_demo_requests'

// --- localStorage helpers (fallback) ---
function getLocalRequests(): DemoRequest[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    return data ? JSON.parse(data) : []
  } catch {
    return []
  }
}

function saveLocalRequests(requests: DemoRequest[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(requests))
}

// --- Public API (async, Supabase-first) ---

export async function getDemoRequests(): Promise<DemoRequest[]> {
  if (isSupabaseConfigured()) {
    try {
      const data = await supabaseClient.getDemoRequests()
      if (data !== null) return data as DemoRequest[]
    } catch (e) {
      console.warn(
        '[DemoRequests] Supabase error, falling back to localStorage:',
        e,
      )
    }
  }
  return getLocalRequests()
}

export async function addDemoRequest(form: {
  name: string
  email: string
  company: string
  phone: string
  employees: string
  message: string
}): Promise<DemoRequest> {
  const newRequest: DemoRequest = {
    id: `demo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    ...form,
    status: 'nueva',
    createdAt: new Date().toISOString(),
    notes: '',
  }

  if (isSupabaseConfigured()) {
    try {
      const result = await supabaseClient.createDemoRequest(form)
      if (result) {
        // Dispatch event for real-time UI updates
        window.dispatchEvent(
          new CustomEvent('cricket-demo-request', { detail: result }),
        )
        return {
          id: result.id,
          name: result.name,
          email: result.email,
          company: result.company,
          phone: result.phone,
          employees: result.employees,
          message: result.message || '',
          status: result.status || 'nueva',
          notes: result.notes || '',
          createdAt: result.created_at,
        }
      }
    } catch (e) {
      console.warn('[DemoRequests] Supabase insert failed, saving locally:', e)
    }
  }

  // Fallback: localStorage
  const requests = getLocalRequests()
  requests.unshift(newRequest)
  saveLocalRequests(requests)
  window.dispatchEvent(
    new CustomEvent('cricket-demo-request', { detail: newRequest }),
  )
  return newRequest
}

export async function updateDemoRequestStatus(
  id: string,
  status: DemoRequest['status'],
): Promise<void> {
  if (isSupabaseConfigured()) {
    try {
      await supabaseClient.updateDemoRequest(id, { status })
      return
    } catch (e) {
      console.warn('[DemoRequests] Supabase update failed:', e)
    }
  }

  // Fallback
  const requests = getLocalRequests()
  const index = requests.findIndex((r) => r.id === id)
  if (index !== -1) {
    requests[index].status = status
    saveLocalRequests(requests)
  }
}

export async function updateDemoRequestNotes(
  id: string,
  notes: string,
): Promise<void> {
  if (isSupabaseConfigured()) {
    try {
      await supabaseClient.updateDemoRequest(id, { notes })
      return
    } catch (e) {
      console.warn('[DemoRequests] Supabase update failed:', e)
    }
  }

  // Fallback
  const requests = getLocalRequests()
  const index = requests.findIndex((r) => r.id === id)
  if (index !== -1) {
    requests[index].notes = notes
    saveLocalRequests(requests)
  }
}

export async function deleteDemoRequest(id: string): Promise<void> {
  if (isSupabaseConfigured()) {
    try {
      await supabaseClient.deleteDemoRequest(id)
      return
    } catch (e) {
      console.warn('[DemoRequests] Supabase delete failed:', e)
    }
  }

  // Fallback
  const requests = getLocalRequests().filter((r) => r.id !== id)
  saveLocalRequests(requests)
}

export async function getNewRequestsCount(): Promise<number> {
  if (isSupabaseConfigured()) {
    try {
      return await supabaseClient.getNewDemoRequestsCount()
    } catch (e) {
      console.warn('[DemoRequests] Supabase count failed:', e)
    }
  }
  return getLocalRequests().filter((r) => r.status === 'nueva').length
}
